import {
  useTimeSlotChoices,
  useUtilityRequestConfiguration,
} from "@features/cx/disconnect/hooks";
import { CreateDisconnectionRequestPayload } from "@features/cx/disconnect/types";
import { DynamicForm } from "@shared/components/DynamicForm";
import { StepHelpers } from "@shared/components/Stepper";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { ChevronLeft, ChevronRight, FileText } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";

interface RequestDetailsStepProps {
  remoteUtilityId: number;
  storageKey: string;
  logTimeUntilStep?: (step: number | string) => void;
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
}

export const RequestDetailsStep: React.FC<RequestDetailsStepProps> = ({
  remoteUtilityId,
  storageKey,
  logTimeUntilStep,
  stepHelpers,
  currentStepIndex = 1,
  onNext,
  onPrevious,
  setValidationError,
  clearValidationError,
  isValidating,
}) => {
  const [isStepDataLoaded, setIsStepDataLoaded] = useState(false);
  const dataLoadedRef = useRef(false);
  const savedStepDataRef = useRef<any>(null);

  // Fetch utility request configuration for reasons
  const { data: utilityConfigData, isLoading: isConfigLoading } =
    useUtilityRequestConfiguration({
      remoteUtilityId,
      requestType: "Disconnect Permanent",
      disablePagination: true,
    });

  // Fetch time slot choices
  const { data: timeSlotData, isLoading: isTimeSlotLoading } =
    useTimeSlotChoices();

  const requestFields: FormField[] = [
    {
      name: "reason",
      label: "Reason",
      type: "select",
      required: true,
      placeholder: "Select a reason",
      options:
        utilityConfigData?.result?.map((config) => ({
          label: config.name,
          value: config.id.toString(),
        })) || [],
      helperText: "Select the primary reason for this bulk request",
      className: "col-span-2",
    },
    {
      name: "scheduledDate",
      label: "Preferred Date",
      type: "datepicker",
      placeholder: "Select date",
      helperText: "Choose preferred date for disconnection",
      className: "col-span-1",
    },
    {
      name: "timeSlot",
      label: "Preferred Time Slot",
      type: "select",
      placeholder: "Select time slot",
      options:
        timeSlotData?.result?.map((slot) => ({
          label: slot.value,
          value: slot.key.toString(),
        })) || [],
      helperText: "Select preferred time window",
      className: "col-span-1",
    },
    {
      name: "consumerRemark",
      label: "Additional Notes",
      type: "textarea",
      placeholder: "Add special instructions or important information...",
      rows: 4,
      helperText: "Provide any additional information for field staff",
      className: "col-span-2",
    },
  ];

  const requestForm = FormService.createForm<CreateDisconnectionRequestPayload>(
    {
      fields: requestFields,
      mode: "onChange",
      defaultValues: {
        requestType: "disconnection",
        reason: "",
        priority: 0,
        scheduledDate: null,
        timeSlot: "",
        consumerRemark: "",
      },
    }
  );

  // Load saved data
  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current) {
      const savedStepData = stepHelpers.getStepData(currentStepIndex);
      savedStepDataRef.current = savedStepData;

      if (savedStepData) {
        FormService.populateForm(requestForm, savedStepData, requestFields);
      }
      dataLoadedRef.current = true;
      setIsStepDataLoaded(true);
    }
  }, [stepHelpers, currentStepIndex, requestForm, requestFields]);

  const handleFormSubmit = useCallback(
    (data: CreateDisconnectionRequestPayload) => {
      if (!data.reason) {
        setValidationError?.("Please select a reason for the request.");
        return;
      }

      // Capture labels for display in Review step
      const selectedReason = utilityConfigData?.result?.find(
        (config) => config.id.toString() === data.reason
      );
      const selectedTimeSlot = timeSlotData?.result?.find(
        (slot) => slot.key.toString() === data.timeSlot
      );

      if (stepHelpers) {
        stepHelpers.setStepData(currentStepIndex, {
          ...data,
          reasonLabel: selectedReason?.name || data.reason,
          timeSlotLabel: selectedTimeSlot?.value || data.timeSlot,
          priority: selectedReason?.extraData?.priorityLevel ?? 0,
          lastUpdated: new Date().toISOString(),
        });
      }

      logTimeUntilStep?.("Request Details");
      clearValidationError?.();
      onNext?.();
    },
    [
      stepHelpers,
      currentStepIndex,
      onNext,
      setValidationError,
      clearValidationError,
      logTimeUntilStep,
      utilityConfigData,
      timeSlotData,
    ]
  );

  const handleFieldChange = (fieldName: string, value: any) => {
    if (stepHelpers) {
      const currentData = stepHelpers.getStepData(currentStepIndex) || {};
      stepHelpers.setStepData(currentStepIndex, {
        ...currentData,
        [fieldName]: value,
      });
    }
  };

  const renderCustomFooter = () => (
    <div className="flex items-center justify-between pt-6 border-t border-gray-200">
      <Button
        onClick={onPrevious}
        variant="outline"
        className="flex items-center space-x-2"
      >
        <ChevronLeft size={16} />
        <span>Previous</span>
      </Button>

      <Button
        onClick={() => handleFormSubmit(requestForm.getValues())}
        disabled={isValidating}
        className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white"
      >
        <span>Next: Review</span>
        <ChevronRight size={16} />
      </Button>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <FileText className="h-5 w-5 text-blue-600" />
        <h3 className="text-lg font-semibold text-gray-800">
          Bulk Disconnection Details
        </h3>
      </div>

      <DynamicForm
        fields={requestFields}
        form={requestForm}
        onSubmit={handleFormSubmit}
        onFieldChange={handleFieldChange}
        config={{
          showSubmitButton: false,
          showResetButton: false,
          showProgress: false,
          classes: FIELD_CLASSES,
        }}
        gridColumns={2}
        renderCustomFooter={renderCustomFooter}
      />
    </div>
  );
};
